// Playing a translation on request (the speaker button on a card).
//
// Streamed TTS arrives as numbered parts (`<id>_<lang>_final_p0`, `_p1`, ...).
// ttsService.playStoredAudio(<id>_<lang>) only picks the newest part, so for a
// replay we play every part in order. If the server never sent audio (typed
// text on an older backend, or history from a previous visit) we ask the REST
// TTS endpoint for it and keep the result for next time.

import { configService } from '../services/config.js';

let current = null; // { token, key }

function partKeys(tts, prefix) {
    const keys = [];
    for (const key of tts.audioQueue.keys()) {
        const m = key.startsWith(`${prefix}_p`) && key.match(/_p(\d+)$/);
        if (m) keys.push([parseInt(m[1], 10), key]);
    }
    return keys.sort((a, b) => a[0] - b[0]).map(([, key]) => key);
}

function storedKeys(tts, messageId, lang) {
    const base = `${messageId}_${lang}`;
    const finals = partKeys(tts, `${base}_final`);
    if (finals.length) return finals;
    if (tts.audioQueue.has(`${base}_final`)) return [`${base}_final`];
    const partials = partKeys(tts, `${base}_partial`);
    if (partials.length) return partials;
    if (tts.audioQueue.has(`${base}_partial`)) return [`${base}_partial`];
    if (tts.audioQueue.has(base)) return [base];
    return [];
}

export function hasAudio(messageId, lang) {
    const tts = window.ttsAudioService;
    return !!tts && storedKeys(tts, messageId, lang).length > 0;
}

async function synthesize(text, lang, key) {
    const tts = window.ttsAudioService;
    const api = window.apiService;
    const headers = { 'Content-Type': 'application/json', ...(await api.getAuthHeaders()) };
    const response = await fetch(`${configService.getApiBaseUrl()}/learning/tts/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            text,
            language: lang,
            messageId: key,
            tts_mode: localStorage.getItem('polyglot-tts-mode') || 'vits',
        }),
    });
    if (!response.ok) throw new Error(`TTS ${response.status}`);
    const bytes = new Uint8Array(await response.arrayBuffer());
    tts.storeAudioForMessage(key, bytes, lang, text);
}

/** Stop whatever this module is playing. */
export function stopPlayback() {
    const tts = window.ttsAudioService;
    if (current && tts) {
        tts.stopMessage(current.key);
    }
    current = null;
}

export function isPlaying(messageId) {
    return !!current && current.messageId === messageId;
}

/**
 * Play the translation for a message.
 * @returns {Promise<void>} resolves when playback ends or is stopped
 */
export async function playTranslation({ messageId, lang, text, onStart, onEnd }) {
    const tts = window.ttsAudioService;
    if (!tts) return;
    stopPlayback();
    tts.clearQueue?.();
    if (tts.currentAudio && !tts.currentAudio.paused) tts.currentAudio.pause();

    const token = Symbol('playback');
    current = { token, key: null, messageId };
    onStart?.();
    try {
        let keys = storedKeys(tts, messageId, lang);
        if (!keys.length && text) {
            await synthesize(text, lang, `${messageId}_${lang}`);
            keys = storedKeys(tts, messageId, lang);
        }
        if (!keys.length) throw new Error('No audio available');
        for (const key of keys) {
            if (current?.token !== token) break;
            current.key = key;
            await tts.playStoredAudio(key);
        }
    } finally {
        if (current?.token === token) current = null;
        onEnd?.();
    }
}

/**
 * Speak a fresh translation automatically when "Read aloud" is on and the
 * server didn't stream audio for it (typed text on an older backend).
 */
export async function speakIfNoStream({ messageId, lang, text, onStart, onEnd, waitMs = 2500 }) {
    await new Promise((r) => setTimeout(r, waitMs));
    if (hasAudio(messageId, lang)) return;
    try {
        await playTranslation({ messageId, lang, text, onStart, onEnd });
    } catch (error) {
        console.warn('Read aloud: no audio for', messageId, error);
    }
}
